import * as Immutable from 'immutable';
import * as contactActions from '@repo/redux/actions/contacts';
import { pluralize } from '@repo/utils/helpers/string';
import { GroupActionSource, ID, UserAction, UserActionState } from '@repo/utils/types';
import { ActionCopyIcon, ActionGroupsIcon, ActionMergeIcon, ActionArchiveIcon } from '@repo/icons';
import { Dispatch } from 'redux';
import { openModal } from '@repo/redux/actions/ui';
import { copyEmails, copyPhoneNumbers } from '../utils';
import { removeContactsFromGroup } from './groupActions';
import Contact from '@repo/utils/models/Contact';
import { isWindows } from '@repo/utils/helpers/platform-checks';

export const archiveContactBatch = (
  contactIDs: Immutable.Set<ID>,
  source: GroupActionSource,
  dispatch: Dispatch,
) => {
  dispatch(contactActions.deleteContactBatch(contactIDs.toArray(), source));
};

export const mergeContactBatch =
  (
    contactIDs: Immutable.Set<ID>,
    source: GroupActionSource,
    dispatch: Dispatch,
    mergedDuplicateID?: ID,
  ) =>
  () => {
    window.app.confirm(
      {
        title: `Merge ${contactIDs.size} ${pluralize(contactIDs.size, 'Profile')}`,
        message: 'Are you sure you want to merge these profiles into one?',
        action: 'Merge',
        variant: 'danger',
      },
      (confirmed) => {
        if (confirmed) {
          dispatch(
            contactActions.mergeContactBatch(contactIDs.toArray(), source, mergedDuplicateID),
          );
        }
      },
    );
  };

const buildContactBatchActions = (
  { selectedContactBatch, selectedContactArray, groupView, isMultiselectView }: UserActionState,
  dispatch: Dispatch,
  contactIsRestricted: (contact: Contact) => boolean,
  source: GroupActionSource,
): Array<UserAction> => {
  if (selectedContactBatch.size < 2 || !isMultiselectView) {
    return [];
  }

  let addToGroupLabel = 'Add to Group';
  let removeFromGroup = null;

  if (!!groupView) {
    addToGroupLabel = 'Add to another Group';
    removeFromGroup = {
      title: 'Remove from Group',
      type: 'action',
      action: () => removeContactsFromGroup(groupView.group, selectedContactBatch, dispatch),
      icon: ActionArchiveIcon,
      keyboard: isWindows() ? 'Backspace' : 'Del',
    };
  }

  return [
    removeFromGroup,
    {
      title: addToGroupLabel,
      type: 'action',
      action: () => dispatch(openModal('addToGroups')),
      icon: ActionGroupsIcon,
      keyboard: 'l',
    },
    {
      title: 'Copy email addresses',
      type: 'action',
      action: copyEmails(selectedContactArray, contactIsRestricted, source),
      icon: ActionCopyIcon,
    },
    {
      title: 'Copy phone numbers',
      type: 'action',
      action: copyPhoneNumbers(selectedContactArray, contactIsRestricted, source),
      icon: ActionCopyIcon,
    },
    {
      title: 'Merge People',
      type: 'action',
      action: mergeContactBatch(selectedContactBatch, source, dispatch),
      icon: ActionMergeIcon,
      keyboard: 'm',
    },
    {
      title: 'Archive People',
      type: 'action',
      action: () => archiveContactBatch(selectedContactBatch, source, dispatch),
      icon: ActionArchiveIcon,
      keyboard: '#',
    },
  ].filter((action) => !!action);
};

export default buildContactBatchActions;
